import React from "react";

interface RewardEntry {
    rewardDate: string;
    reward: number;
    avgHash: number;
}

/**
 * One row per reward date (UTC day): an account with segpool and proxy workers gets a reward row from each
 * for the same date, and the user sees them as one, with the amounts and the hashrates added up.
 */
export const mergeRewardsByDate = <T extends RewardEntry>(rewards: T[]): T[] => {
    const byDate = new Map<string, T>();
    rewards.forEach(entry => {
        const day = new Date(entry.rewardDate).toISOString().slice(0, 10);
        const merged = byDate.get(day);
        if (merged) {
            merged.reward = Number(merged.reward) + Number(entry.reward);
            merged.avgHash = Number(merged.avgHash) + Number(entry.avgHash);
        } else {
            byDate.set(day, { ...entry });
        }
    });
    return Array.from(byDate.values());
};


interface RevenueRewardProps {
    rewards: RewardEntry[];
    coin: string;
}

const RevenueReward: React.FC<RevenueRewardProps> = ({ rewards, coin }) => {

    const sortedRewards = rewards.sort((a, b) => {
        return new Date(b.rewardDate).getTime() - new Date(a.rewardDate).getTime();
    });


    return (
        <div className="p-0">
            <div className="overflow-hidden rounded-xl border border-blue-100 dark:border-slate-700 shadow-sm transition-colors">
                <table className="min-w-full text-blue-500 dark:text-slate-400 text-center">
                    <thead className="text-[10px] sm:text-xs text-blue-700 dark:text-slate-200 uppercase bg-blue-50 dark:bg-slate-800/50 border-b border-blue-100 dark:border-slate-700">
                        <tr>
                            <th scope="col" className="px-3 sm:px-4 md:px-6 py-3 font-semibold tracking-wider">Reward Date</th>
                            <th scope="col" className="px-3 sm:px-4 md:px-6 py-3 font-semibold tracking-wider">Avg Hashrate</th>
                            <th scope="col" className="px-3 sm:px-4 md:px-6 py-3 font-semibold tracking-wider">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rewards.length === 0 ? (
                            <tr>
                                <td colSpan={3} className="text-center py-6 text-slate-400 text-sm">No rewards found</td>
                            </tr>
                        ) : (
                            sortedRewards.map((entry, index) => (
                                <tr key={index} className="bg-white dark:bg-slate-800/50 border-b border-blue-50 dark:border-slate-700/50 hover:bg-blue-50/50 dark:hover:bg-slate-700/30 transition-colors">
                                    <td className="px-3 sm:px-4 md:px-6 py-3 text-xs sm:text-sm font-medium text-blue-600 dark:text-slate-300">
                                        {new Date(entry.rewardDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                                    </td>
                                    <td className="px-3 sm:px-4 md:px-6 py-3 text-xs sm:text-sm font-bold text-[#03436f] dark:text-slate-100">
                                        {entry.avgHash.toFixed(3)} <span className="text-[10px] opacity-50">Th</span>
                                    </td>
                                    <td className="px-3 sm:px-4 md:px-6 py-3">
                                        <span className="inline-block px-2 py-1 text-xs sm:text-sm rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-semibold border border-blue-100 dark:border-blue-900/30">
                                            {coin === "BTC" ? `${entry.reward.toFixed(8)} BTC` : `${entry.reward.toFixed(8)} ${coin}`}
                                        </span>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default RevenueReward;
